import { Injectable } from '@angular/core';
import { GuardResult, MaybeAsync, Router } from '@angular/router';
import { AuthenticationService, AuthScheme } from '@flexyoo/auth';
import {
  EmployeePlanInvitationDataAccessService,
  EmployeeSettingsService,
} from '@flexyoo/employee/data-access';
import { UserAgreementService } from '@flexyoo/employee/ui';
import { map, of, switchMap, take } from 'rxjs';
import { getAppRoutePerRoleDefault } from 'src/app/app-routes-per-role-defaults.constant';
import { RoleEnum } from 'src/app/shared/models/general/general-enum';

@Injectable()
export class LoginGuard {
  constructor(
    protected readonly router: Router,
    private readonly auth: AuthenticationService,
    private readonly employeeSettingsService: EmployeeSettingsService,
    private readonly userAgreementService: UserAgreementService,
    private readonly employeePlanInvitationService: EmployeePlanInvitationDataAccessService
  ) {}

  canActivate(): MaybeAsync<GuardResult> {
    const user = this.auth.getCurrentUser();

    if (!user) {
      return true;
    }

    switch (user.Role) {
      case RoleEnum.Employee: {
        return this.employeeSettingsService.updateAndLoadEmployeeSettings(user.Id).pipe(
          switchMap(({ employeeSettings, tenantSettings }) =>
            this.employeePlanInvitationService.loadEmployeePlanInvitation(user.EmployeeId).pipe(
              switchMap((invitation) => {
                const userAgreementState = this.userAgreementService.checkUserAgreementState(
                  employeeSettings,
                  tenantSettings,
                  invitation
                );

                // Aggregated 'ok' covers dual-plan "one signed agreement is enough".
                if (userAgreementState !== 'ok') {
                  return this.auth.authScheme$.pipe(
                    take(1),
                    map((scheme) => {
                      if (scheme === AuthScheme.Basic) {
                        const redirectedFromEmployeeAccountPage =
                          this.router.url.includes('employee-account');
                        if (redirectedFromEmployeeAccountPage) {
                          setTimeout(() => {
                            this.auth.logout();
                          }, 500);
                        } else {
                          this.auth.logout();
                          return true;
                        }
                      }

                      return true;
                    })
                  );
                }

                const redirectUrl = this.userAgreementService.shouldRedirectToAccountOnLogin(
                  employeeSettings,
                  tenantSettings,
                  invitation
                )
                  ? '/employee-account'
                  : (this.auth.failedUrlToRedirect$.value ??
                    getAppRoutePerRoleDefault(
                      user.Role as RoleEnum,
                      employeeSettings,
                      tenantSettings,
                      invitation
                    ));

                this.router.navigateByUrl(redirectUrl);
                this.auth.failedUrlToRedirect$.next(null);
                return of(false);
              })
            )
          )
        );
      }
      case RoleEnum.Admin:
      case RoleEnum.HR:
      case RoleEnum.Opco:
        return this.router.navigateByUrl(getAppRoutePerRoleDefault(user.Role as RoleEnum));
    }

    this.auth.logout();
    return true;
  }
}
